import { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentApplication = () => {
  const [formData, setFormData] = useState({
    // Student Info
    student_name: '',
    student_email: '',
    student_phone: '',
    grade_level: '',
    school_name: '',
    
    // Parent/Guardian Info
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    preferred_language: '',
    
    // Academic & Goals
    gpa: '',
    sat_score: '',
    college_aspirations: '',
    intended_major: '',
    
    // Background
    first_generation: '',
    financial_aid_needed: '',
    current_challenges: '',
    
    // Additional Info
    extracurriculars: '',
    how_did_you_hear: '',
    why_lantern_prep: ''
  });
  
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Submit as an inquiry with type 'student'
      const applicationData = {
        name: formData.student_name,
        email: formData.student_email,
        phone: formData.student_phone,
        inquiry_type: 'student',
        message: JSON.stringify(formData, null, 2)
      };
      
      await axios.post(`${API}/inquiries`, applicationData);
      toast.success('Application submitted successfully! We\'ll be in touch soon.');
      
      // Reset form
      setFormData({
        student_name: '',
        student_email: '',
        student_phone: '',
        grade_level: '',
        school_name: '',
        parent_name: '',
        parent_email: '',
        parent_phone: '',
        preferred_language: '',
        gpa: '',
        sat_score: '',
        college_aspirations: '',
        intended_major: '',
        first_generation: '',
        financial_aid_needed: '',
        current_challenges: '',
        extracurriculars: '',
        how_did_you_hear: '',
        why_lantern_prep: ''
      });
      
      // Scroll to top
      window.scrollTo(0, 0);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div data-testid="student-application-page">
      <Navbar />
      
      {/* Hero Banner */}
      <section className="bg-primary text-primary-foreground pt-32 pb-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="text-sm font-medium tracking-[0.2em] uppercase text-accent mb-6">
            Scholarship Application
          </div>
          <h1 className="text-5xl md:text-6xl font-heading font-semibold tracking-tight leading-[1.05] mb-8">
            Apply for a Scholarship Seat.
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-primary-foreground/85 max-w-2xl mb-6">
            Scholarship seats are awarded to students from Pinellas and Hillsborough Counties who meet one or more of the following criteria:
          </p>
          <ul className="list-disc pl-6 space-y-2 marker:text-accent text-base md:text-lg leading-relaxed text-primary-foreground/85 max-w-2xl mb-6">
            <li>First-generation to college (no parent has completed a four-year degree)</li>
            <li>Family with demonstrated financial need</li>
            <li>Students navigating significant personal or academic circumstances</li>
          </ul>
          <p className="text-lg md:text-xl leading-relaxed text-primary-foreground/85 max-w-2xl mb-4">
            Selected scholarship students receive the full Lantern Prep engagement at no cost. Every seat — paying or scholarship — represents the same commitment of time, expertise, and resources.
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-primary-foreground/85 max-w-2xl mb-4">
            Selection is based on merit, motivation, and circumstance.
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-primary-foreground/85 max-w-2xl mb-8">
            The application is detailed by design. We read every submission carefully.
          </p>
          <div className="flex gap-4 items-center text-sm">
            <span className="text-accent font-medium tracking-wide">EN</span>
            <span className="text-primary-foreground/30">|</span>
            <a href="/apply/es" data-testid="lang-switch-es" className="text-primary-foreground/70 hover:text-accent transition-colors tracking-wide">
              ES
            </a>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
          <form onSubmit={handleSubmit} data-testid="scholarship-application-form" className="bg-card border border-border p-8 md:p-12">
            <div className="space-y-8">
              
              {/* Student Information */}
              <div>
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Student Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="student_name" className="text-foreground font-medium mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="student_name"
                      name="student_name"
                      type="text"
                      required
                      value={formData.student_name}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="Student's full name"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="student_email" className="text-foreground font-medium mb-2 block">
                      Email *
                    </Label>
                    <Input
                      id="student_email"
                      name="student_email"
                      type="email"
                      required
                      value={formData.student_email}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="student@email.com"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="student_phone" className="text-foreground font-medium mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="student_phone"
                      name="student_phone"
                      type="tel"
                      required
                      value={formData.student_phone}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="(813) 555-1234"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="grade_level" className="text-foreground font-medium mb-2 block">
                      Current Grade *
                    </Label>
                    <Select
                      required
                      value={formData.grade_level}
                      onValueChange={(value) => setFormData({ ...formData, grade_level: value })}
                    >
                      <SelectTrigger className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm">
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="9th">9th Grade</SelectItem>
                        <SelectItem value="10th">10th Grade</SelectItem>
                        <SelectItem value="11th">11th Grade</SelectItem>
                        <SelectItem value="12th">12th Grade</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="school_name" className="text-foreground font-medium mb-2 block">
                      High School Name *
                    </Label>
                    <Input
                      id="school_name"
                      name="school_name"
                      type="text"
                      required
                      value={formData.school_name}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="Your high school"
                    />
                  </div>
                </div>
              </div>

              {/* Parent/Guardian Information */}
              <div>
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Parent/Guardian Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="parent_name" className="text-foreground font-medium mb-2 block">
                      Parent/Guardian Name *
                    </Label>
                    <Input
                      id="parent_name"
                      name="parent_name"
                      type="text"
                      required
                      value={formData.parent_name}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="Parent's full name"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="parent_email" className="text-foreground font-medium mb-2 block">
                      Parent Email *
                    </Label>
                    <Input
                      id="parent_email"
                      name="parent_email"
                      type="email"
                      required
                      value={formData.parent_email}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="parent@email.com"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="parent_phone" className="text-foreground font-medium mb-2 block">
                      Parent Phone *
                    </Label>
                    <Input
                      id="parent_phone"
                      name="parent_phone"
                      type="tel"
                      required
                      value={formData.parent_phone}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="(813) 555-1234"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="preferred_language" className="text-foreground font-medium mb-2 block">
                      Preferred Language *
                    </Label>
                    <Select
                      required
                      value={formData.preferred_language}
                      onValueChange={(value) => setFormData({ ...formData, preferred_language: value })}
                    >
                      <SelectTrigger className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="english">English</SelectItem>
                        <SelectItem value="spanish">Spanish / Español</SelectItem>
                        <SelectItem value="both">Both / Ambos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Academic Background */}
              <div>
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Academic Background
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="gpa" className="text-foreground font-medium mb-2 block">
                      Current GPA (if known)
                    </Label>
                    <Input
                      id="gpa"
                      name="gpa"
                      type="text"
                      value={formData.gpa}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="3.5"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="sat_score" className="text-foreground font-medium mb-2 block">
                      SAT Score (if taken)
                    </Label>
                    <Input
                      id="sat_score"
                      name="sat_score"
                      type="text"
                      value={formData.sat_score}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="1200"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="college_aspirations" className="text-foreground font-medium mb-2 block">
                      College Aspirations *
                    </Label>
                    <Textarea
                      id="college_aspirations"
                      name="college_aspirations"
                      required
                      value={formData.college_aspirations}
                      onChange={handleChange}
                      rows={3}
                      placeholder="What colleges are you interested in? What are your goals?"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="intended_major" className="text-foreground font-medium mb-2 block">
                      Intended Major or Field of Interest
                    </Label>
                    <Input
                      id="intended_major"
                      name="intended_major"
                      type="text"
                      value={formData.intended_major}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="e.g., Engineering, Pre-Med, Business, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Background & Fit */}
              <div>
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Background & Program Fit
                </h2>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="first_generation" className="text-foreground font-medium mb-2 block">
                      Are you a first-generation college student? *
                    </Label>
                    <Select
                      required
                      value={formData.first_generation}
                      onValueChange={(value) => setFormData({ ...formData, first_generation: value })}
                    >
                      <SelectTrigger className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="yes">Yes - Neither parent has a 4-year degree</SelectItem>
                        <SelectItem value="no">No - One or both parents have a 4-year degree</SelectItem>
                        <SelectItem value="unsure">Unsure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="financial_aid_needed" className="text-foreground font-medium mb-2 block">
                      Will you need financial aid or scholarships for college? *
                    </Label>
                    <Select
                      required
                      value={formData.financial_aid_needed}
                      onValueChange={(value) => setFormData({ ...formData, financial_aid_needed: value })}
                    >
                      <SelectTrigger className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="maybe">Maybe</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="current_challenges" className="text-foreground font-medium mb-2 block">
                      What are your biggest challenges in preparing for college? *
                    </Label>
                    <Textarea
                      id="current_challenges"
                      name="current_challenges"
                      required
                      value={formData.current_challenges}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Tell us what feels overwhelming or unclear about the college process..."
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="extracurriculars" className="text-foreground font-medium mb-2 block">
                      Extracurricular Activities, Clubs, or Leadership
                    </Label>
                    <Textarea
                      id="extracurriculars"
                      name="extracurriculars"
                      value={formData.extracurriculars}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Sports, clubs, volunteer work, jobs, family responsibilities, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Additional Information
                </h2>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="how_did_you_hear" className="text-foreground font-medium mb-2 block">
                      How did you hear about Lantern Prep?
                    </Label>
                    <Input
                      id="how_did_you_hear"
                      name="how_did_you_hear"
                      type="text"
                      value={formData.how_did_you_hear}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="School counselor, friend, social media, etc."
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="why_lantern_prep" className="text-foreground font-medium mb-2 block">
                      Why are you interested in Lantern Prep? *
                    </Label>
                    <Textarea
                      id="why_lantern_prep"
                      name="why_lantern_prep"
                      required
                      value={formData.why_lantern_prep}
                      onChange={handleChange}
                      rows={4}
                      placeholder="What draws you to our program? What do you hope to gain?"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-6 border-t border-border">
                <button
                  type="submit"
                  data-testid="scholarship-submit"
                  disabled={loading}
                  className="w-full md:w-auto bg-primary text-primary-foreground px-12 py-4 rounded-sm font-medium tracking-wide hover:bg-accent hover:text-accent-foreground transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
                
                <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
                  Lantern Prep provides educational consulting and advocacy support. Nothing on this site constitutes legal advice or creates an attorney–client relationship.
                </p>
              </div>
            </div>
          </form>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default StudentApplication;
